<template>
  <v-card outlined tile class="fill-height" :loading="$apollo.queries.notes.loading">
    <v-card-title>
      {{ title }}
      <v-spacer/>
      <v-btn color="primary" small @click="openCreate" v-if="hasRole('ROLE_CREATE_NOTE')">
        {{ $t('AddNote') }}
      </v-btn>
      <v-dialog v-model="showDialog" persistent max-width="600px">
        <v-card>
          <v-card-title>
            <span class="headline">{{ editingId ? $t('Edit') : $t('AddNote') }}</span>
          </v-card-title>
          <v-card-text>
            <NoteForm
              :errors="formErrors"
              :values="formItem"
              ref="noteForm"
            />
          </v-card-text>
          <v-card-actions>
            <v-btn text @click="showDialog = false">{{ $t('Close') }}</v-btn>
            <v-spacer/>
            <v-btn class="mainAccent lighten-3" dark @click="resetForm">{{ $t('Reset') }}</v-btn>
            <v-btn color="primary" :loading="saving" @click="submitForm">{{ $t('Submit') }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card-title>
    <v-card-text>
      <template v-if="!$apollo.queries.notes.loading && noteCollection.length > 0">
        <v-row>
          <v-col col="12" md="6" v-for="note in noteCollection" :key="note.id">
            <NoteCard
              :item="note"
              :handle-deleted="deleteNote"
              @showEdit="openEdit"
            />
          </v-col>
        </v-row>
      </template>
      <template v-else-if="!$apollo.queries.notes.loading">
        <p>Keine Daten verfügbar</p>
      </template>
    </v-card-text>
  </v-card>
</template>

<script>
import gql from 'graphql-tag';
import NoteCard from '@/components/note/Card';
import NoteForm from '@/components/note/Form';

const NOTES_QUERY = gql`
  query notes($speaker: String, $customer: String, $invoice: String, $venue: String, $seminar: String) {
    notes(speaker: $speaker, customer: $customer, invoice: $invoice, venue: $venue, seminar: $seminar) {
      collection {
        id
        content
        createdAt
        updatedAt
        createdBy {
          id
          profile{
            id
            firstName
            lastName
          }
        }
        updatedBy {
          id
          profile{
            id
            firstName
            lastName
          }
        }
      }
    }
  }
`;

const CREATE_NOTE = gql`
  mutation createNote($content: String!, $speaker: String, $customer: String, $invoice: String, $venue: String, $seminar: String) {
    createNote(input: { content: $content, speaker: $speaker, customer: $customer, invoice: $invoice, venue: $venue, seminar: $seminar }) {
      note { id content }
    }
  }
`;

const UPDATE_NOTE = gql`
  mutation updateNote($id: ID!, $content: String!) {
    updateNote(input: { id: $id, content: $content }) {
      note { id content }
    }
  }
`;

const DELETE_NOTE = gql`
  mutation deleteNote($id: ID!) {
    deleteNote(input: { id: $id }) {
      clientMutationId
    }
  }
`;

export default {
  name: 'NoteListAndAddGql',
  components: { NoteCard, NoteForm },
  props: {
    defaultItemProps: {
      type: Object,
      required: true,
    },
    showMessage: {
      type: Function,
      required: true,
    },
    objectType: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      required: false,
      default: function () {
        return this.$t('Notes');
      },
    },
  },
  data() {
    return {
      showDialog: false,
      formItem: { content: '' },
      formErrors: {},
      saving: false,
      editingId: null,
    };
  },
  computed: {
    noteCollection() {
      return this.notes ? this.notes.collection : [];
    },
    authHeader() {
      return this.$store.state.authentication.loggedIn
        ? `Bearer ${this.$store.state.authentication.token}`
        : '';
    },
  },
  methods: {
    openCreate() {
      this.formItem = { content: '' };
      this.editingId = null;
      this.formErrors = {};
      this.showDialog = true;
    },
    openEdit(note) {
      this.formItem = { content: note.content };
      this.editingId = note.id;
      this.formErrors = {};
      this.showDialog = true;
    },
    resetForm() {
      if (this.$refs.noteForm) {
        this.$refs.noteForm.$v.$reset();
      }
      this.formItem = { content: '' };
    },
    async submitForm() {
      this.$refs.noteForm.$v.$touch();
      if (this.$refs.noteForm.$v.$invalid) return;

      const content = this.$refs.noteForm.$v.item.$model.content;
      this.saving = true;

      try {
        if (this.editingId) {
          await this.$apollo.mutate({
            mutation: UPDATE_NOTE,
            variables: { id: this.editingId, content },
            context: { headers: { authorization: this.authHeader } },
          });
          this.showMessage(this.$t('successfullyUpdated'));
        } else {
          await this.$apollo.mutate({
            mutation: CREATE_NOTE,
            variables: { content, ...this.defaultItemProps },
            context: { headers: { authorization: this.authHeader } },
          });
          this.showMessage(this.$t('successfullyCreated'));
        }
        this.showDialog = false;
        this.$apollo.queries.notes.refetch();
      } catch (error) {
        console.error(error);
      } finally {
        this.saving = false;
      }
    },
    async deleteNote(item) {
      try {
        await this.$apollo.mutate({
          mutation: DELETE_NOTE,
          variables: { id: item.id },
          context: { headers: { authorization: this.authHeader } },
        });
        this.showMessage(this.$t('successfullyDeleted'));
        this.$apollo.queries.notes.refetch();
      } catch (error) {
        console.error(error);
      }
    },
  },
  apollo: {
    notes() {
      return {
        query: NOTES_QUERY,
        variables() {
          return this.defaultItemProps;
        },
        context: {
          headers: {
            authorization: this.$store.state.authentication.loggedIn
              ? `Bearer ${this.$store.state.authentication.token}`
              : '',
          },
        },
      };
    },
  },
};
</script>
